/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/** @jsx jsx */
import { jsx } from "@emotion/core"

import { AttributeCluster } from "types/models/menu"
import { ItemAttributeValue } from "types/models/item_attribute"
import { Input } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { useState } from "react"
import { notShowToPax } from "./AttributeDrawer.utils"
import styles from "./AttributeDrawer.modules.css"

export interface AttributeDrawerFreeTextProps extends FormComponentProps {
  attributeCluster: AttributeCluster
  selectedAttributes?: ItemAttributeValue[]
  handleChangeAttributes: (value?: ItemAttributeValue[]) => void
  itemID?: string
  disableFormRule?: boolean
}

export function AttributeDrawerFreeText({
  form,
  attributeCluster,
  selectedAttributes,
  handleChangeAttributes,
  itemID,
  disableFormRule,
}: AttributeDrawerFreeTextProps) {
  const textMap = useTranslationsMapTyped({
    infoNotShownToPax: "itemAttributes.info.notShownToPax",
    warningRequired: "itemAttributes.error.required",
    warningFreeTextExceed: "itemAttributes.error.freeTextExceed",
  })

  const [textValueLen, setTextValueLen] = useState<number>(0)
  const nameLabel = attributeCluster.nameLocalization || attributeCluster.name

  const { getFieldDecorator } = form

  const initialValue = selectedAttributes?.find(
    (key: ItemAttributeValue) => key.attributeClusterID === attributeCluster.id,
  )

  const handleTextChange = (event: { target: { value: string } }) => {
    const freeTextValue = event.target.value
    const attributeSelected: ItemAttributeValue = {
      itemID,
      status: attributeCluster?.status || "Enabled",
      value: freeTextValue,
      attributeClusterID: attributeCluster?.id,
    }
    let finalValue = selectedAttributes?.filter(
      key => key.attributeClusterID !== attributeCluster?.id,
    )
    if (freeTextValue.length) {
      finalValue = finalValue
        ? finalValue.concat(attributeSelected)
        : [attributeSelected]
    }
    setTextValueLen(freeTextValue.length)
    handleChangeAttributes(finalValue)
  }

  return (
    <div data-testid="attribute-drawer-freetext" className={styles.formWrapper}>
      <Form.Item label={nameLabel}>
        {getFieldDecorator(`attributeForm.${nameLabel}`, {
          initialValue: initialValue?.value,
          rules: disableFormRule
            ? undefined
            : [
                {
                  required:
                    attributeCluster?.attributeClusterOption?.isMandatory,
                  message: textMap.warningRequired,
                },
                {
                  max: attributeCluster.metadata?.freeTextMetadata?.maxLength,
                  message: textMap.warningFreeTextExceed
                    .replace("<current>", String(textValueLen))
                    .replace(
                      "<limit>",
                      String(
                        attributeCluster.metadata?.freeTextMetadata?.maxLength,
                      ),
                    ),
                },
              ],
        })(
          <Input.TextArea
            data-testid="input-attribute-free-text"
            placeholder="Please Type"
            onChange={handleTextChange}
          />,
        )}
      </Form.Item>
      {!attributeCluster.attributeClusterOption.isShownToPax &&
        notShowToPax(textMap.infoNotShownToPax)}
    </div>
  )
}
